// ════════════════════════════════════════════════════════════════════════════
//  ManDana — where a thing lives, asked in one place
//  ---------------------------------------------------------------------------
//  md-contain.ts holds the RULES of the tree and touches nothing. This holds the
//  tree itself: it knows the five homes containment was spelled in, reads them
//  all as one shape, and is the only door that moves anything.
//
//  THE FIVE SPELLINGS, AND WHY THEY STAY. A pursuit points up with `domainId`,
//  a project with `pursuitId` (falling back to `domainId`), a desk with
//  `scopeRef`, a record with `pid`. Rewriting all four into one synced field
//  would be a migration on every device for no behaviour, so the field stays
//  where it is and the QUESTION moves here. The reading is uniform; the storage
//  is not, and that difference is the two homes md-record.ts already named,
//  showing through again.
//
//  WHAT WAS ACTUALLY BROKEN. md-board.ts wrote `pid` only when the scope was a
//  project, so a desk scoped to a pursuit or a domain pointed up with `scopeRef`
//  and had no `pid` at all — meaning the answer to "what is above this?"
//  depended on WHICH KIND the answer happened to be. That is the bug this file
//  closes, and it is why `pid` is now DERIVED (walk up to the nearest project)
//  rather than written by hand. md-project.ts already does exactly this with a
//  project's `domainId`, which it derives from its pursuit.
//
//  A TREE WITHOUT A MOVE IS A PRISON. Every kind gets `mdParentSet`, gated by
//  mdContainCheck, refusing BY NAME — `rank`, `cycle`, `self`, `missing` — the
//  way md-transport.ts requires of everything else that can say no.
//
//  THE INDEX IS BUILT ONCE PER QUESTION. `mdParentOf` is a direct read and
//  costs one lookup. `mdParentChildren` and `mdParentSubtree` walk every home,
//  so they build the map once and answer from it — the shape md-database.ts
//  landed on after a 5,110-key account materialised 138,015 keys to draw one
//  screen.
//
//  DAY RECORDS ARE READ FROM THE DAY MAPS, not from storage per record, for the
//  reason md-resolve.ts states: the caller is a render.
// ════════════════════════════════════════════════════════════════════════════

/** Why a move was refused. Named, never numbered. */
type MdParentReason = 'missing' | 'malformed' | 'self' | 'rank' | 'cycle'
  | 'unsupported' | 'storage_full';

type MdParentResult = { ok: true } | { ok: false; reason: MdParentReason };

(function (g: any) {
  'use strict';

  const keyOf = (ref: unknown): string => {
    if (typeof ref === 'string') return g.mdRecordRefParse && g.mdRecordRefParse(ref) ? ref : '';
    return g.mdRecordRefKey ? g.mdRecordRefKey(ref) : '';
  };

  const parse = (ref: unknown): any => (g.mdRecordRefParse ? g.mdRecordRefParse(keyOf(ref)) : null);

  /* Day-anchored refs carry their day in the key, and a record that moves days
     would otherwise change identity mid-walk. The tree is keyed on the DAYLESS
     form throughout, which is also the shape mdWikiTargetKey binds. */
  const flat = (ref: unknown): string => {
    const r = parse(ref);
    return r ? `${r.type}:${r.id}` : '';
  };

  /* ── reading one parent ───────────────────────────────────────────────── */

  function mdParentOf(ref: unknown): string | null {
    const r = parse(ref);
    if (!r) return null;
    if (r.type === 'domain') return g.mdDomainById && g.mdDomainById(r.id) ? null : null;
    if (r.type === 'pursuit') {
      const row = g.mdPursuitById ? g.mdPursuitById(r.id) : null;
      return row && row.domainId ? `domain:${row.domainId}` : null;
    }
    if (r.type === 'project') {
      const row = g.mdProjectById ? g.mdProjectById(r.id) : null;
      if (!row) return null;
      if (row.pursuitId && g.mdPursuitById && g.mdPursuitById(row.pursuitId)) {
        return `pursuit:${row.pursuitId}`;
      }
      return row.domainId ? `domain:${row.domainId}` : null;
    }
    if (r.type === 'board') {
      const scope = g.mdBoardScope ? g.mdBoardScope(r.id) : null;
      return typeof scope === 'string' && flat(scope) ? flat(scope) : null;
    }
    if (r.type === 'database') {
      const db = g.mdDatabaseRead ? g.mdDatabaseRead(r.id) : null;
      return db && typeof db.scopeRef === 'string' && flat(db.scopeRef) ? flat(db.scopeRef) : null;
    }
    if (r.type === 'craft' || r.type === 'manifest') {
      const row = g.mdManifestById ? g.mdManifestById(r.id) : null;
      return row && typeof row.scopeRef === 'string' && flat(row.scopeRef)
        ? flat(row.scopeRef) : null;
    }
    /* Anything else is a record, in one of its two homes. `parentRef` is the
       general pointer; `pid` is the project it is in, which is the same thing
       whenever the parent IS a project and is derived from the chain otherwise. */
    const rec = recordRow(r.id);
    if (!rec) return null;
    if (typeof rec.parentRef === 'string' && flat(rec.parentRef)) return flat(rec.parentRef);
    return typeof rec.pid === 'string' && rec.pid ? `project:${rec.pid}` : null;
  }

  /** A record row from either home, or null. */
  function recordRow(uid: string): any | null {
    const backlog = g.mdProjectRecordById ? g.mdProjectRecordById(uid) : null;
    if (backlog) return backlog;
    const days = g.mdStoreAll ? g.mdStoreAll('records') : null;
    if (!days || typeof days !== 'object') return null;
    for (const day of Object.keys(days)) {
      const list = days[day];
      if (!Array.isArray(list)) continue;
      const hit = list.find((rec: any) => rec && rec.uid === uid);
      if (hit) return hit;
    }
    return null;
  }

  /* ── the whole tree, once ─────────────────────────────────────────────── */

  /** Every containment edge in the account: child key -> parent key.

      Built by walking every home rather than by reading one index, because
      there is no one index — that is the situation, not a shortcut. */
  function mdParentIndex(): Map<string, string | null> {
    const map = new Map<string, string | null>();
    const put = (child: string, parent: string | null) => {
      if (child) map.set(child, parent || null);
    };

    for (const d of (g.mdDomainIndex ? g.mdDomainIndex() : [])) put(`domain:${d.id}`, null);
    for (const p of (g.mdPursuitIndex ? g.mdPursuitIndex() : [])) {
      put(`pursuit:${p.id}`, p.domainId ? `domain:${p.domainId}` : null);
    }
    for (const p of (g.mdProjectIndex ? g.mdProjectIndex() : [])) {
      const viaPursuit = p.pursuitId && g.mdPursuitById && g.mdPursuitById(p.pursuitId);
      put(`project:${p.id}`, viaPursuit ? `pursuit:${p.pursuitId}`
        : p.domainId ? `domain:${p.domainId}` : null);
    }
    for (const b of (g.mdBoardIndex ? g.mdBoardIndex() : [])) {
      put(`board:${b.id}`, typeof b.scopeRef === 'string' ? flat(b.scopeRef) || null : null);
    }
    for (const db of (g.mdDatabaseList ? g.mdDatabaseList() : [])) {
      put(`database:${db.id}`, typeof db.scopeRef === 'string' ? flat(db.scopeRef) || null : null);
    }
    for (const c of (g.mdManifestList ? g.mdManifestList() : [])) {
      put(`craft:${c.instance}`, typeof c.scopeRef === 'string' ? flat(c.scopeRef) || null : null);
    }

    const addRecord = (rec: any) => {
      if (!rec || typeof rec.uid !== 'string') return;
      const type = g.mdRecordType ? g.mdRecordType(rec) : 'note';
      const parent = typeof rec.parentRef === 'string' && flat(rec.parentRef) ? flat(rec.parentRef)
        : typeof rec.pid === 'string' && rec.pid ? `project:${rec.pid}` : null;
      put(`${type}:${rec.uid}`, parent);
    };
    for (const rec of (g.mdProjectRecordIndex ? g.mdProjectRecordIndex() : [])) addRecord(rec);
    const days = g.mdStoreAll ? g.mdStoreAll('records') : null;
    if (days && typeof days === 'object') {
      for (const day of Object.keys(days)) {
        const list = days[day];
        if (Array.isArray(list)) for (const rec of list) addRecord(rec);
      }
    }
    return map;
  }

  /** Everything one thing directly contains. */
  function mdParentChildren(ref: unknown, index?: Map<string, string | null>): string[] {
    const self = flat(ref);
    if (!self) return [];
    const map = index || mdParentIndex();
    const out: string[] = [];
    for (const [child, parent] of map) if (parent === self) out.push(child);
    return out.sort();
  }

  /** Everything a delete would reach: the thing and its containment subtree. */
  function mdParentSubtree(ref: unknown): string[] {
    const self = flat(ref);
    if (!self) return [];
    const map = mdParentIndex();
    const kids = new Map<string, string[]>();
    for (const [child, parent] of map) {
      if (!parent) continue;
      const list = kids.get(parent);
      if (list) list.push(child); else kids.set(parent, [child]);
    }
    for (const list of kids.values()) list.sort();
    return g.mdContainSubtree(self, (key: string) => kids.get(key) || []);
  }

  /* ── the move ─────────────────────────────────────────────────────────── */

  /** Does this ref name something that exists? */
  function present(ref: string): boolean {
    const r = parse(ref);
    if (!r) return false;
    if (r.type === 'domain') return !!(g.mdDomainById && g.mdDomainById(r.id));
    if (r.type === 'pursuit') return !!(g.mdPursuitById && g.mdPursuitById(r.id));
    if (r.type === 'project') return !!(g.mdProjectById && g.mdProjectById(r.id));
    if (r.type === 'board') {
      return !!(g.mdBoardIndex && g.mdBoardIndex().some((b: any) => b.id === r.id));
    }
    if (r.type === 'database') return !!(g.mdDatabaseRead && g.mdDatabaseRead(r.id));
    if (r.type === 'craft' || r.type === 'manifest') {
      return !!(g.mdManifestById && g.mdManifestById(r.id));
    }
    return !!recordRow(r.id);
  }

  /** Move one thing under another, or to the root. Refuses by name.

      The only door. Every per-kind setter underneath it is still exported and
      still works — this is the one that knows the RULE, so a surface that wants
      the rule enforced asks here and a surface that is restoring a document from
      the wire does not. */
  function mdParentSet(ref: unknown, parent: unknown): MdParentResult {
    const child = flat(ref);
    if (!child) return { ok: false, reason: 'malformed' };
    if (!present(child)) return { ok: false, reason: 'missing' };

    const toRoot = parent == null || parent === '';
    const parentKey = toRoot ? '' : flat(parent);
    if (!toRoot && !parentKey) return { ok: false, reason: 'malformed' };
    if (!toRoot && !present(parentKey)) return { ok: false, reason: 'missing' };

    const index = mdParentIndex();
    const check = g.mdContainCheck(child, toRoot ? null : parentKey,
      (key: string) => (index.has(key) ? index.get(key) || null : null));
    if (!check.ok) return { ok: false, reason: check.reason };

    return apply(child, toRoot ? null : parentKey, index);
  }

  /** Write the pointer into whichever field that kind keeps it in. */
  function apply(child: string, parentKey: string | null,
    index: Map<string, string | null>): MdParentResult {
    const r = parse(child);
    if (!r) return { ok: false, reason: 'malformed' };

    const wrote = (ok: boolean): MdParentResult => {
      if (ok) return { ok: true };
      const fault = typeof g.mdStorageHealth === 'function' ? g.mdStorageHealth().lastFault : null;
      return { ok: false, reason: fault && fault.quota ? 'storage_full' : 'unsupported' };
    };

    if (r.type === 'domain') return { ok: false, reason: 'rank' };

    if (r.type === 'pursuit') {
      const p = parse(parentKey);
      if (!p || p.type !== 'domain') return { ok: false, reason: 'unsupported' };
      return wrote(!!(g.mdPursuitSetDomain && g.mdPursuitSetDomain(r.id, p.id)));
    }

    if (r.type === 'project') {
      const p = parse(parentKey);
      if (p && p.type === 'pursuit') {
        return wrote(!!(g.mdProjectSetPursuit && g.mdProjectSetPursuit(r.id, p.id)));
      }
      if (p && p.type === 'domain') {
        const cleared = g.mdProjectSetPursuit ? g.mdProjectSetPursuit(r.id, null) : false;
        return wrote(!!cleared && !!(g.mdProjectSetDomain && g.mdProjectSetDomain(r.id, p.id)));
      }
      return { ok: false, reason: 'unsupported' };
    }

    if (r.type === 'board') {
      return wrote(!!(g.mdBoardSetScope && g.mdBoardSetScope(r.id, parentKey)));
    }
    if (r.type === 'database') {
      return wrote(!!(g.mdDatabaseSetScope && g.mdDatabaseSetScope(r.id, parentKey)));
    }
    if (r.type === 'craft' || r.type === 'manifest') {
      return wrote(!!(g.mdManifestSetScope && g.mdManifestSetScope(r.id, parentKey)));
    }

    /* A record. `parentRef` is the pointer; `pid` follows it up to the nearest
       project, so the backlog a record is listed in stays true without anybody
       writing it twice. */
    return wrote(setRecordParent(r.id, parentKey, index));
  }

  /** The nearest project at or above a key, walking the index. */
  function projectAbove(key: string | null, index: Map<string, string | null>): string {
    let at = key;
    for (let hops = 0; at && hops < 64; hops += 1) {
      const r = parse(at);
      if (r && r.type === 'project') return r.id;
      at = index.has(at) ? index.get(at) || null : null;
    }
    return '';
  }

  function setRecordParent(uid: string, parentKey: string | null,
    index: Map<string, string | null>): boolean {
    const pid = projectAbove(parentKey, index);
    const backlog = g.mdProjectRecordById ? g.mdProjectRecordById(uid) : null;
    if (backlog) return writeBacklogParent(uid, parentKey, pid);
    return writeDayParent(uid, parentKey, pid);
  }

  function writeBacklogParent(uid: string, parentKey: string | null, pid: string): boolean {
    if (!g.mdProjectRecordSetParent) return false;
    return !!g.mdProjectRecordSetParent(uid, parentKey, pid);
  }

  function writeDayParent(uid: string, parentKey: string | null, pid: string): boolean {
    const days = g.mdStoreAll ? g.mdStoreAll('records') : null;
    if (!days || typeof days !== 'object') return false;
    for (const day of Object.keys(days)) {
      const list = days[day];
      if (!Array.isArray(list)) continue;
      const at = list.findIndex((rec: any) => rec && rec.uid === uid);
      if (at < 0) continue;
      const next = list.map((rec: any) => (rec && rec.uid === uid
        ? {
          ...rec,
          ...(parentKey ? { parentRef: parentKey } : { parentRef: undefined }),
          ...(pid ? { pid } : { pid: undefined }),
          updatedAt: Date.now(),
        }
        : rec));
      /* undefined does not survive JSON, which is exactly what "no parent"
         should mean in the document — so the field disappears rather than
         becoming a null another device would have to interpret. */
      return !!(g.mdStoreWrite && g.mdStoreWrite('records', day, next));
    }
    return false;
  }

  /** The nearest project at or above a ref — what `pid` means now that it is
      derived. Exported because md-board.ts writes that field and must not grow
      its own copy of the walk. */
  function mdParentProjectAbove(ref: unknown): string {
    const key = flat(ref);
    return key ? projectAbove(key, mdParentIndex()) : '';
  }

  Object.assign(g, {
    mdParentOf, mdParentIndex, mdParentChildren, mdParentSubtree, mdParentSet,
    mdParentProjectAbove,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdParentOf, mdParentIndex, mdParentChildren, mdParentSubtree,
      mdParentSet, mdParentProjectAbove };
  }
})(typeof window !== 'undefined' ? window : globalThis);
